import { UseStepsProps, useSteps } from '@chakra-ui/react';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { WizardStepProps } from '../components/WizardStep/WizardStep';

export type WizardHookProps = UseStepsProps;
export type WizardHook = {
  wizardRef: MutableRefObject<WizardRef>;
  loading: boolean;
  error: Error | undefined;
  activeStep: number;
  canGoToNextStep: boolean;
  progress: number;
  hasPrevious: boolean;
  hasNext: boolean;
  setActiveStep(step: number): void;
  isActiveStep(step: number): boolean;
  isCompleteStep(step: number): boolean;
  isIncompleteStep(step: number): boolean;
  nextStep(): void;
  previousStep(): void;
  validateStep(fn: StepValidator): Promise<void> | void;
};

type StepValidator = <T>(data?: T | undefined) => Promise<boolean> | boolean;
const DefaultStepValidator: StepValidator = () => true;

export type WizardRef = {
  next: () => Promise<void> | void;
  validate: (data?: unknown) => Promise<void> | void;
};
export type InternalWizardRef = {
  count: number;
  position: number;
  step: WizardStepProps | null;
  next: () => Promise<void> | void;
  validate: (data?: unknown) => Promise<void> | void;
  loading: boolean;
  error: Error | undefined;
  activeStep: number;
  canGoToNextStep: boolean;
};
export const useWizard = ({
  count = 0,
  index = 0,
}: WizardHookProps): WizardHook => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [stepValidator, setStepValidator] =
    useState<StepValidator>(DefaultStepValidator);
  const [canGoToNextStep, setCanGoToNextStep] = useState<boolean>(false);
  const {
    activeStep,
    setActiveStep,
    isActiveStep,
    isCompleteStep,
    isIncompleteStep,
    activeStepPercent,
    goToNext: nextStep,
    goToPrevious: previousStep,
  } = useSteps({ count, index });

  const wizardRef = useRef<InternalWizardRef>({
    count: 0,
    position: index,
    step: null,
    next: nextStep,
    validate: () => {},
    activeStep,
    canGoToNextStep,
    loading,
    error,
  });

  const validateStep = async () => {
    const isValid = await stepValidator();
    setCanGoToNextStep(isValid);
  };

  const validateCallback = useCallback(
    async (data: unknown | undefined) => {
      try {
        const checkFn =
          wizardRef.current.step?.validate || DefaultStepValidator;
        setStepValidator(checkFn);
        const isValid = await checkFn(data);
        setCanGoToNextStep(isValid);
        setError(undefined);
      } catch (err) {
        setError(err as Error);
        setCanGoToNextStep(false);
      }
    },
    [setCanGoToNextStep, setError],
  );

  const nextCallback = useCallback(async () => {
    try {
      if (wizardRef.current.step?.onComplete) {
        setLoading(true);
        await wizardRef.current.step.onComplete();
      }

      setCanGoToNextStep(false);
      nextStep();
      setError(undefined);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [nextStep]);

  useEffect(() => {
    wizardRef.current.validate = validateCallback;
    wizardRef.current.next = nextCallback;
  }, [
    wizardRef.current.step,
    nextStep,
    setCanGoToNextStep,
    setStepValidator,
    validateCallback,
    nextCallback,
  ]);

  return {
    hasNext: activeStep < count - 1,
    hasPrevious: activeStep > 0,
    wizardRef,
    loading,
    error,
    activeStep,
    canGoToNextStep,
    progress: activeStepPercent,
    setActiveStep,
    isActiveStep,
    isCompleteStep,
    isIncompleteStep,
    nextStep,
    previousStep,
    validateStep,
  };
};
